/**
 * **Layar pelayan** — ambil pesanan di meja lewat HP (K4, Fase 4).
 *
 * ### ⭐ Apa yang membuatnya BUKAN sekadar kasir tanpa tombol bayar
 * Pembedanya ditegakkan **server**, bukan layar ini: sejak 2026-08-03 rute `tenders` dan `close`
 * menuntut izin `payment:take`, dan pelayan tak memilikinya. Layar ini hanya **berhenti menawarkan**
 * apa yang memang akan ditolak — itu sopan santun antarmuka, bukan kontrol. ⛔ Kalau suatu saat
 * panel tender muncul di sini karena "biar praktis", yang menahan tetap server.
 *
 * ### ⛔⛔ Tanggal usaha dari SERVER, bukan dari HP
 * Kasir mendapatkannya dari sesi kas; pelayan tak punya sesi kas — ia tak memegang uang. Kalau HP
 * yang memutuskan, pesanan pukul 01:30 mendarat di hari yang salah (§2), dan tiap HP pelayan bisa
 * menjawab berbeda tanpa satu pun merasa salah. Yang menemukannya nanti adalah selisih saat tutup
 * buku — bukan galat apa pun.
 *
 * ### ⚠️ Alur pelayan berhenti di KIRIM, bukan di bayar
 * Ia membuka meja → menambah baris → **kirim ke dapur**, lalu kembali ke lantai. Bill-nya diurus
 * kasir. Karena itu layar ini pulang ke daftar meja sesudah mengirim, bukan bertahan di bill:
 * pelayan yang tertahan di layar bill akan menaruh HP-nya dan lupa mengirim pesanan berikutnya.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, type MenuItem } from '../api/client';
import { formatRupiah } from '../format';
import { openLive, type LiveState } from '../api/live';
import { staffSession } from '../api/session';
import { GuestCallPanel } from './GuestCallPanel';
import { MenuBrowser } from './MenuBrowser';
import { TableDashboard } from './TableDashboard';

export interface WaiterShellProps {
  readonly outletCode: string;
  readonly deviceCode: string;
  readonly menu?: readonly MenuItem[];
  readonly onLoggedOut: () => void;
}

interface DraftLine {
  readonly lineId: string;
  readonly code: string;
  readonly name: string;
  readonly qty: string;
}

export function WaiterShell(p: WaiterShellProps) {
  const { t } = useTranslation();
  const [menu, setMenu] = useState<readonly MenuItem[]>(p.menu ?? []);
  /** ⭐ Kode produk yang HABIS — hanya untuk MEREDUPKAN kartu; pelayan tak boleh mengubahnya. */
  const [soldOut, setSoldOut] = useState<ReadonlySet<string>>(new Set());
  const [businessDate, setBusinessDate] = useState<string | undefined>(undefined);
  const [dayClosed, setDayClosed] = useState(false);
  const [orderId, setOrderId] = useState<string | null>(null);
  const [tableCode, setTableCode] = useState<string | null>(null);
  const [lines, setLines] = useState<readonly DraftLine[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [liveState, setLiveState] = useState<LiveState>('connecting');
  const [reloadToken, setReloadToken] = useState(0);
  const [search, setSearch] = useState('');
  const [department, setDepartment] = useState<string | null>(null);
  const [category, setCategory] = useState<string | null>(null);

  /* ⛔ Tanggal usaha DULU — tanpanya tak boleh ada pesanan yang dibuka sama sekali. */
  useEffect(() => {
    void (async () => {
      try {
        const d = await api.businessDate(p.outletCode);
        setBusinessDate(d.businessDate);
        setDayClosed(d.dayClosed);
      } catch {
        setError(t('waiter.noBusinessDate'));
      }
    })();
  }, [p.outletCode, t]);

  useEffect(() => {
    if (p.menu !== undefined && p.menu.length > 0) return;
    void (async () => {
      try {
        const k = await api.catalog(p.outletCode, 'dine_in', businessDate ?? '');
        setMenu(k.items);
        /*
         * ⭐⭐⭐ **Tanda HABIS ikut dibaca pelayan** (FB-86-03, UAT 2026-08-29).
         *
         * ⛔⛔ Sebelum ini layar pelayan membuang seluruh katalog kecuali `items`, jadi ia TAK
         * PERNAH tahu apa yang habis. Server memang menolak barisnya (`ORDER-409-SOLD_OUT`) — tapi
         * penolakan itu datang SESUDAH pelayan menjanjikannya ke tamu dan menekan kartunya di
         * depan meja. Dan karena kartunya tampak normal, ia akan mencobanya lagi.
         *
         * ⚠️ K28 menyatakannya eksplisit: item yang habis **tetap tampil** (redup + HABIS), tak
         * disaring — yang lenyap terbaca sebagai menu rusak.
         */
        setSoldOut(new Set(k.soldOut ?? []));
      } catch {
        setError(t('cashier.loadFailed'));
      }
    })();
  }, [p.outletCode, p.menu, businessDate, t]);

  /* ⭐ Meja & panggilan tamu bergerak karena ORANG LAIN — kanal dorong yang menggerakkannya. */
  const refresh = useCallback(() => { setReloadToken((n) => n + 1); }, []);
  useEffect(() => {
    const staf = staffSession();
    if (staf === undefined) return;
    const channel = openLive({
      outletCode: p.outletCode,
      staffToken: staf.token,
      onNudge: (kind) => { if (kind === undefined || kind === 'table') refresh(); },
      onState: setLiveState,
    });
    return () => { channel.close(); };
  }, [p.outletCode, refresh]);

  async function openTable(code: string, guestCount: number): Promise<void> {
    if (businessDate === undefined) return;
    setError(null);
    try {
      const o = await api.openOrder({
        outletCode: p.outletCode,
        orderNo: `W/${code}/${String(Date.now()).slice(-6)}`,
        businessDate,
        orderType: 'dine_in',
        entryChannel: 'pos',
        priceChannel: 'dine_in',
        guestCount,
        tableCode: code,
        deviceCode: p.deviceCode,
      });
      setOrderId(o.id);
      setTableCode(code);
      setLines([]);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function addLine(item: MenuItem): Promise<void> {
    if (orderId === null) return;
    try {
      const r = await api.addLine(orderId, { productCode: item.code, qty: '1' });
      setLines((l) => [
        ...l,
        { lineId: String(r.lineId ?? ''), code: item.code, name: item.name, qty: '1' },
      ]);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function send(): Promise<void> {
    if (orderId === null) return;
    try {
      await api.sendOrder(orderId, {
        /* ⚠️ `actor` masih di body untuk rute lama; pelakunya tetap DIVERIFIKASI dari token staf. */
        actor: { actorKind: 'staff', code: '', name: '' },
      });
      /*
       * ⭐ Pulang ke daftar meja, bukan bertahan di bill. Pelayan yang tertahan di layar bill akan
       * menaruh HP-nya dan lupa mengirim pesanan meja berikutnya — dan yang menunggu adalah tamu
       * yang pesanannya tak pernah sampai ke dapur.
       */
      setOrderId(null);
      setTableCode(null);
      setLines([]);
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div className="waiter" data-testid="waiter">
      <header className="waiter-bar">
        <strong>{t('waiter.title')}</strong>
        {businessDate !== undefined ? (
          /* ⭐ Ditampilkan terus-menerus: pelayan berhak tahu pesanannya masuk hari yang mana. */
          <span className="waiter-date" data-testid="waiter-business-date">{businessDate}</span>
        ) : null}
        {liveState === 'degraded' ? (
          <span className="warn" data-testid="waiter-degraded">{t('waiter.degraded')}</span>
        ) : null}
        <button type="button" className="ghost" onClick={p.onLoggedOut}>{t('common.logout')}</button>
      </header>

      {error !== null ? <p className="error" data-testid="waiter-error">{error}</p> : null}
      {/*
        ⛔ Hari yang sudah di-settlement: dikatakan di DEPAN, bukan dibiarkan jadi galat saat menekan
        "kirim". Pelayan yang sudah mencatat lima item lalu ditolak akan mencatatnya ulang di kertas
        — dan yang di kertas tak pernah sampai ke laporan.
      */}
      {dayClosed ? <p className="error" data-testid="waiter-day-closed">{t('waiter.dayClosed')}</p> : null}

      {orderId === null ? (
        <>
          <GuestCallPanel outletCode={p.outletCode} />
          <TableDashboard
            key={reloadToken}
            outletCode={p.outletCode}
            onOpenTable={(code, guests) => void openTable(code, guests)}
            onEnterOrder={(id) => { setOrderId(id); }}
          />
        </>
      ) : (
        <>
          <div className="waiter-order-bar">
            <span data-testid="waiter-table">{tableCode ?? ''}</span>
            <button
              type="button"
              className="ghost"
              data-testid="waiter-back"
              onClick={() => { setOrderId(null); setTableCode(null); setLines([]); }}
            >
              {t('waiter.back')}
            </button>
          </div>

          <ul className="waiter-lines" data-testid="waiter-lines">
            {lines.map((l) => (
              <li key={l.lineId} data-testid={`waiter-line-${l.code}`}>
                <span>{l.qty}×</span> <span>{l.name}</span>
              </li>
            ))}
          </ul>

          {/*
            ⛔⛔ Tak ada tombol BAYAR, tak ada laci, tak ada total. Bukan karena disembunyikan —
            server menolaknya (`payment:take`). Yang ada di sini cuma yang memang boleh dikerjakan.
          */}
          <button
            type="button"
            className="primary"
            data-testid="waiter-send"
            disabled={lines.length === 0}
            onClick={() => void send()}
          >
            {t('waiter.send')}
          </button>

          <input
            className="waiter-search"
            data-testid="waiter-search"
            value={search}
            placeholder={t('cashier.searchMenu')}
            onChange={(e) => { setSearch(e.target.value); }}
          />
          <MenuBrowser
            menu={menu}
            search={search}
            department={department}
            category={category}
            onDepartment={setDepartment}
            onCategory={setCategory}
            onPick={(item) => void addLine(item)}
            formatPrice={formatRupiah}
            soldOut={soldOut}
            /* ⛔ TANPA `onHoldItem`: menandai/mencabut HABIS adalah perbuatan KASIR (K28 —
               gerbangnya longgar tapi berjejak). Pelayan melihat, tak mengubah. */
          />
        </>
      )}
    </div>
  );
}
